/**
 * The wine list for guests as a printable HTML page (docs/15-getraenkekarte.md): layouts A, B and C
 * over the parts of `drinksCard`. Used by the app to print and by `npm run card` for the preview.
 */
import { CARD_COLOR, cardFootnotes, lineSize, ownAllergens, partNote } from "@lagerlog/core";
import type { CardEntry, CardGrouping, CardLine, CardPart, CardSection } from "@lagerlog/core";

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
/**
 * Prices in A and B are columns of a table: always with cents and without the euro sign (the head
 * says „Preise in Euro“), so the commas stand one below the other. A private cellar shows bottles.
 */
export const price = (l: CardLine) => (l.price === null ? `${l.bottles ?? 0} Fl.` : (l.price / 100).toFixed(2).replace(".", ","));
/** C is set like text: with the euro sign, whole euros without cents, bottles and glasses alike. */
export const priced = (l: CardLine) =>
  l.price === null ? price(l) : `${l.price % 100 === 0 ? String(l.price / 100) : (l.price / 100).toFixed(2).replace(".", ",")} €`;
/** "trinkreif 2024–2030", only on the list of a private cellar. */
export const drinkOf = (l: CardLine | undefined) => (!l || l.price !== null || (!l.drinkFrom && !l.drinkTo) ? null : `trinkreif ${l.drinkFrom ?? "…"}–${l.drinkTo ?? "…"}`);
/** Whether the vintages of a wine differ in their drinking window: then each line shows its own. */
export const perLine = (e: CardEntry) => new Set(e.lines.map((l) => drinkOf(l))).size > 1;
export const drink = (e: CardEntry) => (perLine(e) ? null : drinkOf(e.lines[0]));
/** The size where it is not the usual bottle: „1/8 l“, „0,375 l“, „Magnum 1,5 l“; else "". */
export const size = (l: CardLine) => lineSize(l);
/** In the part of open wines the year goes to the name, the glass to the right; no card text. */
export const isOpen = (e: CardEntry) => e.lines[0]?.ml !== undefined;
const openYear = (e: CardEntry) => (isOpen(e) && e.lines[0]!.vintage !== null ? ` ${e.lines[0]!.vintage}` : "");
export const year = (l: CardLine) => (l.vintage === null ? "" : String(l.vintage));
export const origin = (e: CardEntry, withCountry: boolean) => [e.region, withCountry ? e.country : null].filter(Boolean).join(", ");
/** Allergens on the card (unless the card keeps them in a list for the staff): those not said for the whole part. */
let allergensOnCard = true;
const allergensOf = (e: CardEntry, p: CardPart | undefined) => (allergensOnCard && p ? ownAllergens(e, p) : []);
export const facts = (e: CardEntry, withCountry: boolean, p?: CardPart) =>
  [...(e.extra ?? []), origin(e, withCountry), e.quality ?? null, e.grapes.join(", "), e.taste && e.taste !== "trocken" ? e.taste : null, e.organic ? "Bio" : null, drink(e), allergensOf(e, p).length ? `enthält ${allergensOf(e, p).join(", ")}` : null]
    .filter(Boolean)
    .join(" · ");
/** Footnote marks of the additives, after the name: „¹˒⁸“ as <sup>1,8</sup>. */
export const marks = (e: CardEntry) => (e.notes?.length ? e.notes.join(",") : "");
/** Producer and name; a prepared drink has only its name. */
const head = (e: CardEntry, extra = "") =>
  `${e.producer ? `<b>${esc(e.producer)}</b> · ${esc(e.wine)}` : `<b>${esc(e.wine)}</b>`}${extra}${marks(e) ? `<sup>${marks(e)}</sup>` : ""}`;

/** The glass sizes of a section, smallest first: they become columns. */
export const glassSizes = (sec: CardSection) => [...new Map(sec.groups.flatMap((g) => g.entries.flatMap((e) => e.lines)).map((l) => [l.ml!, l.size])).entries()].sort((a, b) => a[0] - b[0]).map(([, s]) => s);
/** Prices of an open wine in the columns of its section; empty where it has no such glass. */
const glassCells = (e: CardEntry, sizes: string[]) => sizes.map((sz) => `<span class="pr">${(() => { const l = e.lines.find((x) => x.size === sz); return l ? price(l) : ""; })()}</span>`).join("");
const glassHead = (sizes: string[], lead: string) => `<div class="glass-head" style="grid-template-columns: ${lead} ${sizes.map(() => "16mm").join(" ")}"><span></span><span></span>${sizes.map((s) => `<span>${s}</span>`).join("")}</div>`;

/** A, open: number, name and year, facts below; one column per glass size. */
function classicOpen(e: CardEntry, sizes: string[], p: CardPart) {
  return `<div class="a-entry"><div class="a-line" style="grid-template-columns: var(--no) 1fr ${sizes.map(() => "16mm").join(" ")}"><span class="no">${e.lines[0]!.listNo ?? ""}</span><span class="name">${head(e, openYear(e))}</span>${glassCells(e, sizes)}</div>
    <div class="a-more"><div class="facts">${esc(facts(e, true, p))}</div></div></div>`;
}

/** B, open: one line per wine, one column per glass size. */
function compactOpen(e: CardEntry, sizes: string[]) {
  return `<div class="b-line" style="grid-template-columns: var(--no) 1fr ${sizes.map(() => "16mm").join(" ")}"><span class="no">${e.lines[0]!.listNo ?? ""}</span><span class="name">${esc(e.producer)} <span class="w">${esc(e.wine)}</span>${openYear(e)}${marks(e) ? `<sup>${marks(e)}</sup>` : ""} <span class="o">${esc([origin(e, true), drink(e)].filter(Boolean).join(" · "))}</span></span>${glassCells(e, sizes)}</div>`;
}

/**
 * A: three blocks side by side (Martin, 18.10.2026): the card number, the text (name, facts, card
 * text in italics, straight below each other), and the offers (year or size and price, one per line).
 * The text spans every line of offers; what is left of it goes into the last, flexible row, so the
 * offers stay close together and nothing leaves a gap under the name.
 */
function classic(e: CardEntry, withCountry: boolean, p?: CardPart) {
  const one = isOpen(e) || e.plain;
  const offers = e.lines
    .map(
      (l, i) =>
        `<span class="no" style="grid-row: ${i + 1}">${i === 0 || !one ? (l.listNo ?? "") : ""}</span>` +
        (one
          ? `<span class="yr" style="grid-row: ${i + 1}">${size(l)}</span>`
          : `<span class="yr" style="grid-row: ${i + 1}">${year(l)}${size(l) ? `<small>${size(l)}</small>` : ""}${perLine(e) ? `<small>${drinkOf(l) ?? ""}</small>` : ""}</span>`) +
        `<span class="pr" style="grid-row: ${i + 1}">${price(l)}</span>`,
    )
    .join("");
  const text = e.text && (!isOpen(e) || e.plain) ? `<div class="text">${esc(e.text)}</div>` : "";
  return `<div class="a-entry a-line${perLine(e) ? " drinks" : ""}${e.plain ? " plain" : ""}" style="grid-template-rows: repeat(${e.lines.length}, auto) 1fr">
    <div class="a-body"><div class="name">${head(e, openYear(e))}</div><div class="facts">${esc(facts(e, withCountry, p))}</div>${text}</div>${offers}
  </div>`;
}

/** B: one line per bottle, nothing else; for the long card and the table tent. */
function compact(e: CardEntry) {
  return e.lines
    .map(
      (l) => `<div class="b-line"><span class="no">${l.listNo ?? ""}</span><span class="name">${esc(e.producer)} <span class="w">${esc(e.wine)}</span>${marks(e) ? `<sup>${marks(e)}</sup>` : ""} <span class="o">${esc([origin(e, true), drinkOf(l)].filter(Boolean).join(" · "))}</span></span><span class="yr">${year(l)}</span><span class="sz">${size(l) || (e.recipe ? "" : "0,75 l")}</span><span class="pr">${price(l)}</span></div>`,
    )
    .join("");
}

/** C: the wine as a small story: name large, the text, facts small, price below. */
function story(e: CardEntry, p?: CardPart) {
  // One vintage: it goes into the title. Several: one line each, the year bold in its own column.
  const years = new Set(e.lines.map((l) => l.vintage));
  const single = years.size === 1;
  const title = `${esc(e.wine)}${single && e.lines[0]!.vintage !== null ? ` ${e.lines[0]!.vintage}` : ""}${marks(e) ? `<sup>${marks(e)}</sup>` : ""}`;
  // One line without a number: the price goes right into the title row, nothing is left alone below.
  const inTitle = e.lines.length === 1 && !e.lines[0]!.listNo;
  const lines = inTitle
    ? []
    : (() => {
        // One line per offer, in fixed columns, so the prices stand one below the other (Martin, 18.10.2026).
        const one = isOpen(e) || e.plain;
        const sized = e.lines.some((l) => size(l) !== "");
        const drinks = perLine(e);
        // Each column as wide as before (text plus its gap, in mm) while there is room; when the column
        // of C is too narrow, all gaps shrink in proportion, but never below the text: nothing wraps.
        const mm = [single ? 0 : 16, sized ? 26 : 0, drinks ? 38 : 0].filter((w) => w > 0);
        const cols = [...mm.map((w) => `minmax(max-content, ${w}fr)`), "max-content"].join(" ");
        const width = `min(100%, ${mm.reduce((a, b) => a + b, 0) + 16}mm)`;
        return e.lines.map(
          (l, i) =>
            `<span class="no">${(one ? i === 0 && l.listNo : l.listNo) ? `Nr. ${l.listNo}` : ""}</span><span class="c-offer grid" style="grid-template-columns: ${cols}; width: ${width}">${single ? "" : `<span class="c-year">${year(l)}</span>`}${sized ? `<span class="c-size">${size(l)}</span>` : ""}${drinks ? `<span class="c-size">${drinkOf(l) ?? ""}</span>` : ""}<b>${priced(l)}</b></span>`,
        );
      })();
  return `<div class="c-entry">
    <div class="c-name"><span>${title}</span>${inTitle ? `<span class="c-title-price">${size(e.lines[0]!) ? `<span class="c-size">${size(e.lines[0]!)}</span>` : ""}<b>${priced(e.lines[0]!)}</b></span>` : ""}</div>
    ${e.producer ? `<div class="c-prod">${esc(e.producer)}</div>` : ""}
    <div class="c-facts">${esc([isOpen(e) ? null : (CARD_COLOR[e.color] ?? e.color), e.quality ?? null, e.grapes.join(", "), e.taste, e.analysis ?? null, drink(e), ...(e.extra ?? []), allergensOf(e, p).length ? `enthält ${allergensOf(e, p).join(", ")}` : null].filter(Boolean).join(" · "))}</div>
    ${e.text && (!isOpen(e) || e.plain) ? `<p class="c-text">${esc(e.text)}</p>` : ""}
    ${lines.length ? `<div class="c-lines">${lines.map((l) => `<div class="c-line">${l}</div>`).join("")}</div>` : ""}
  </div>`;
}

/**
 * A section as a table whose head is its heading (and the glass sizes): when the section runs over
 * the end of a page, the browser repeats the head at the top of the next one.
 */
const section = (head: string, body: string) => `<table class="sec"><thead><tr><td>${head}</td></tr></thead><tbody><tr><td>${body}</td></tr></tbody></table>`;
/** A section's heading; a rubric's only section has none (the part says it). */
const h2 = (s: CardSection) => (s.title ? `<h2>${s.title}</h2>` : "");

export interface CardVariant {
  key: "a" | "b" | "c";
  file: string;
  title: string;
  grouping: CardGrouping;
  render: (s: CardSection, part: CardPart) => string;
}

/** The three layouts of docs/15-getraenkekarte.md, each with the grouping it was shown with. */
export const CARD_VARIANTS: CardVariant[] = [
  {
    key: "a",
    file: "a-farbe-herkunft-mit-text",
    title: "A · nach Farbe, darin nach Land · mit Kartentext",
    grouping: "colorOrigin",
    render: (s, p) =>
      p.open
        ? section(`${h2(s)}${glassHead(glassSizes(s), "var(--no) 1fr")}`, s.groups.map((g) => g.entries.map((e) => classicOpen(e, glassSizes(s), p)).join("")).join(""))
        : section(h2(s), s.groups.map((g) => `${g.title ? `<h3>${g.title}</h3>` : ""}${g.entries.map((e) => classic(e, false, p)).join("")}`).join("")),
  },
  {
    key: "b",
    file: "b-farbe-kompakt",
    title: "B · nach Farbe · kompakt, eine Zeile je Glas oder Flasche",
    grouping: "color",
    render: (s, p) =>
      p.open
        ? section(`${h2(s)}${glassHead(glassSizes(s), "var(--no) 1fr")}`, s.groups.map((g) => g.entries.map((e) => compactOpen(e, glassSizes(s))).join("")).join(""))
        : section(h2(s), s.groups.map((g) => g.entries.map(compact).join("")).join("")),
  },
  {
    key: "c",
    file: "c-herkunft-erzaehlend",
    title: "C · nach Herkunft, darin nach Region · erzählend",
    grouping: "origin",
    render: (s, p) => section(h2(s), s.groups.map((g) => `${g.title ? `<h3>${g.title}</h3>` : ""}<div class="c-grid">${g.entries.map((e) => story(e, p)).join("")}</div>`).join("")),
  },
];

const CSS = `
@page { size: A4; margin: 16mm 16mm 18mm; }
* { box-sizing: border-box; }
@media screen { body { padding: 10mm 12mm; } }
body { margin: 0; font: 10pt/1.35 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #2a2522; }
header { text-align: center; margin-bottom: 8mm; }
header .house { font-size: 26pt; letter-spacing: 0.08em; font-variant: small-caps; }
header .sub { font-size: 9pt; color: #8a7d74; letter-spacing: 0.2em; text-transform: uppercase; }
.variant { margin-top: 2mm; text-align: center; font: 7.5pt system-ui, sans-serif; color: #b0a59c; }
h1 { font-size: 20pt; font-weight: normal; text-align: center; letter-spacing: 0.1em; margin: 9mm 0 0; break-after: avoid; }
/* The parts flow on (C1): a part starts a page only when its title and first entries no longer fit. */
.part-sub { text-align: center; font-size: 8.5pt; color: #8a7d74; font-style: italic; break-after: avoid; }
table.sec { width: 100%; border-collapse: collapse; }
table.sec > * > tr > td { padding: 0; }
/* Nothing may hang below the last wine of a section: a margin that runs past the end of a page
   makes the browser start a page with only the repeated heading. */
table.sec > tbody > tr > td > :last-child, table.sec > tbody > tr > td > :last-child > .c-entry { margin-bottom: 0; padding-bottom: 0; }
h2 { font-size: 15pt; font-weight: normal; font-variant: small-caps; letter-spacing: 0.06em; color: #7b2d3a; border-bottom: 0.6pt solid #c9b8ab; padding-bottom: 1.5mm; margin: 7mm 0 3mm; break-after: avoid; }
h3 { font-size: 9pt; font-weight: normal; text-transform: uppercase; letter-spacing: 0.18em; color: #8a7d74; margin: 4mm 0 1.5mm; break-after: avoid; }
.no { font-variant-numeric: tabular-nums; color: #7b2d3a; }
.pr { font-variant-numeric: tabular-nums; text-align: right; }
.glass-head { display: grid; font-size: 8pt; color: #8a7d74; text-align: right; margin: -1.5mm 0 1mm; }
/* A */
.a-entry { break-inside: avoid; margin: 0 0 3mm; }
.a-line { display: grid; grid-template-columns: 9mm 1fr 22mm 14mm; align-items: baseline; }
.a-line .no { text-align: center; }
.a-line .yr { text-align: right; font-variant-numeric: tabular-nums; line-height: 1.15; }
/* The text spans all lines of offers and the flexible row below them. */
.a-body { grid-column: 2; grid-row: 1 / -1; min-width: 0; padding-right: 3mm; }
.a-body .facts { font-size: 8.5pt; color: #8a7d74; }
.a-body .text { font-style: italic; font-size: 9pt; margin-top: 0.6mm; }
.a-entry.a-line { align-items: start; }
.a-entry.a-line > .no { grid-column: 1; }
.a-entry.a-line > .yr { grid-column: 3; line-height: 1.35; }
.a-entry.a-line > .yr small { display: block; line-height: 1.15; }
.a-entry.a-line > .pr { grid-column: 4; line-height: 1.35; }
.a-line small { font-size: 7.5pt; color: #8a7d74; }
.a-line.drinks { grid-template-columns: 9mm 1fr 34mm 14mm; margin-bottom: 1mm; }
/* Not a wine: the size column holds „Flasche 0,33 l“, „Halbe 0,5 l“. */
.a-line.plain { grid-template-columns: var(--no) 1fr 30mm 14mm; }
.unnumbered .a-line.drinks { grid-template-columns: 0 1fr 34mm 14mm; }
.a-more { margin-left: 9mm; margin-right: 36mm; }
.a-more .facts { font-size: 8.5pt; color: #8a7d74; }
.a-more .text { font-style: italic; font-size: 9pt; margin-top: 0.6mm; }
/* B */
.b-line { display: grid; grid-template-columns: 9mm 1fr 12mm 24mm 17mm; align-items: baseline; padding: 0.9mm 0; border-bottom: 0.3pt dotted #d8ccc2; }
.b-line .no { text-align: center; }
.b-line .w { font-style: italic; }
.b-line .o { font-size: 8pt; color: #8a7d74; }
.b-line .yr, .b-line .sz { font-variant-numeric: tabular-nums; text-align: right; font-size: 9pt; color: #5a4f48; }
/* C */
/* Two columns of the same width; whatever is long wraps inside its column. */
.c-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 3mm 8mm; }
.c-entry { break-inside: avoid; padding-bottom: 2mm; min-width: 0; overflow-wrap: anywhere; }
.c-name { font-size: 12pt; display: flex; justify-content: space-between; align-items: baseline; gap: 4mm; }
.c-name > span:first-child { min-width: 0; }
.c-title-price { display: flex; gap: 3mm; align-items: baseline; font-size: 9.5pt; white-space: nowrap; }
.c-prod { font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.12em; color: #8a7d74; }
.c-text { margin: 1.2mm 0 1mm; font-size: 9pt; }
.c-facts { font-size: 8pt; color: #8a7d74; font-style: italic; }
.c-lines { font-size: 9pt; margin-top: 1mm; }
.c-line { display: grid; grid-template-columns: 14mm 1fr; align-items: baseline; }
.c-line .no { font-size: 8pt; }
/* No card numbers anywhere on the list: their column goes, nothing is left blank. */
body { --no: 9mm; }
.unnumbered { --no: 0px; }
.unnumbered .c-line { display: block; }
.unnumbered .c-line .no { display: none; }
.unnumbered .a-line { grid-template-columns: 0 1fr 22mm 14mm; }
.unnumbered .a-line.plain { grid-template-columns: 0 1fr 30mm 14mm; }
.unnumbered .a-more { margin-left: 0; }
.unnumbered .b-line { grid-template-columns: 0 1fr 12mm 24mm 17mm; }
.c-offer { display: flex; gap: 5mm; align-items: baseline; }
.c-offer.grid { display: grid; gap: 0; }
.c-offer, .c-offer > *, .c-line .no { white-space: nowrap; }
.c-offer .sep { color: #b0a59c; }
.c-year { font-weight: 600; font-size: 10.5pt; font-variant-numeric: tabular-nums; }
.c-size { color: #5a4f48; }
footer { margin-top: 8mm; font-size: 7.5pt; color: #8a7d74; text-align: center; }
footer .notes { margin-bottom: 1mm; }
sup { font-size: 6.5pt; color: #8a7d74; margin-left: 0.3mm; }
`;

/** Whether any wine on the card has a number; if not, the number column is left out. */
export const numbered = (parts: CardPart[]) => parts.some((p) => p.sections.some((s) => s.groups.some((g) => g.entries.some((e) => e.lines.some((l) => l.listNo)))));

/** Whether the card has more than wine: then it is a „Getränkekarte“. */
export const drinksOnCard = (parts: CardPart[]) => parts.some((p) => p.key !== undefined && p.key !== "offen" && p.key !== "flasche" && p.key !== "wein");

/** What the card is called on its head: Weinkarte, Getränkekarte, Kellerliste. */
export const cardName = (parts: CardPart[], own?: boolean) => (own ? "Kellerliste" : drinksOnCard(parts) ? "Getränkekarte" : "Weinkarte");

/** The footer's line of footnotes: „1 mit Farbstoff · 8 koffeinhaltig“, empty without. */
export const footnoteLine = (parts: CardPart[]) => cardFootnotes(parts).map((f) => `${f.n} ${f.text}`).join(" · ");

/** A whole page: header, the parts, footer. `label` names the variant under the footer (preview only). `allergens: "list"` keeps them off the card (a list for the staff). */
export function cardHtml(parts: CardPart[], variant: CardVariant, o: { house: string; date: Date; label?: boolean; private?: boolean; allergens?: "card" | "list" }): string {
  allergensOnCard = o.allergens !== "list";
  const name = cardName(parts, o.private);
  const notes = o.private ? "" : footnoteLine(parts);
  return `<!doctype html><html lang="de"><meta charset="utf-8"><title>${name} ${esc(o.house)}</title><style>${CSS}</style><body class="${numbered(parts) ? "" : "unnumbered"}">
    <header><div class="house">${esc(o.house)}</div><div class="sub">${o.private ? "Kellerliste" : `${name} · Preise in Euro`}</div></header>
    ${parts.map((p) => `<section class="part">${o.private ? "" : `<h1>${p.title}</h1>`}${partNote(p, allergensOnCard) ? `<div class="part-sub">${esc(partNote(p, allergensOnCard))}</div>` : ""}${p.sections.map((s) => variant.render(s, p)).join("")}</section>`).join("")}
    <footer>${notes ? `<div class="notes">${esc(notes)}</div>` : ""}${o.private ? "Flaschen im Keller" : "Alle Preise inkl. MwSt. · Jahrgänge können sich ändern"} · Stand ${o.date.toLocaleDateString("de-DE")}</footer>
    ${o.label ? `<div class="variant">${variant.title}</div>` : ""}
  </body></html>`;
}

/** The list of allergens for the staff, when they are not on the card. */
export function allergenHtml(rows: { part: string; name: string; allergens: string[] }[], house: string, date: Date): string {
  const body = rows.map((r) => `<tr><td>${esc(r.part)}</td><td>${esc(r.name)}</td><td>${esc(r.allergens.join(", ") || "–")}</td></tr>`).join("");
  return `<!doctype html><html lang="de"><meta charset="utf-8"><title>Allergene ${esc(house)}</title><style>body{font:10pt system-ui,sans-serif;margin:12mm}table{border-collapse:collapse;width:100%}td,th{border-bottom:0.3pt solid #ccc;padding:1.2mm;text-align:left;vertical-align:top}</style><body><h1>Allergene · ${esc(house)}</h1><p>Für das Personal, Stand ${date.toLocaleDateString("de-DE")}</p><table><thead><tr><th>Teil</th><th>Getränk</th><th>Allergene</th></tr></thead><tbody>${body}</tbody></table></body></html>`;
}
